import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell,
  TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { API_BASE_URL } from '@/api/apiConfig';
import { Loader2, AlertCircle, Search, Copy, Check, FileImage, RefreshCw, Trash2, Download } from 'lucide-react';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import apiClient from '@/api/crawlerApi';
import { AiUsageStatus } from '@/api/dashboardApi';
import ExcelJS from 'exceljs';
import { sseService } from '@/services/sseService';
import { CustomAltEditor } from '@/components/custom-alt/CustomAltEditor';
import { customAltApi } from '@/api/customAltApi';

// 인증 토큰이 필요한 이미지를 Blob URL로 보여주는 컴포넌트
function AuthenticatedImage({
  src, alt, className
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!src) return;
    
    setLoading(true);
    setFailed(false);
    
    // 외부 URL인 경우 인증 없이 바로 표시
    const isExternalUrl = src.startsWith('http') && !src.startsWith(BASE + '/');
    if (isExternalUrl) {
      setBlobUrl(src);
      setLoading(false);
      return;
    }
    
    // 자체 서버 URL인 경우 인증 헤더로 fetch
    let objectUrl: string | null = null;
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    
    fetch(src, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => {
        if (!res.ok) throw new Error('fetch failed');
        return res.blob();
      })
      .then(blob => {
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setLoading(false);
      })
      .catch(() => {
        setFailed(true);
        setLoading(false);
      });

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (loading) {
    return <div className={`${className} bg-gray-100 animate-pulse`} />;
  }
  
  if (failed || (!blobUrl && !src)) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center text-xs text-gray-400`}>
        No Image
      </div>
    );
  }
  
  return <img src={blobUrl || src} alt={alt} className={className} onError={() => { setFailed(true); }} />;
}

// ── 타입 ────────────────────────────────────────────────────
type SourceType = 'imageUrl' | 'upload';
type SortBy = 'CREATED_AT' | 'UPDATED_AT';
type SortDir = 'ASC' | 'DESC';

interface OpenApiUsageItem {
  id: number;
  requestId?: string;
  url: string;
  imageSrc: string;
  displayImageUrl?: string;
  sourceType: SourceType;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  generatedAlt: string | null;
  originalAlt?: string | null;
  altGenerationType?: string;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
  useContextProfile?: boolean;
  channel?: string;
  cachecnt?: number;  // 캐시 사용 횟수
  customAltText: string | null;  // 커스텀 ALT 텍스트
}

interface AltTypeStats {
  ai: number;
  fallback: number;
  pending: number;
  error: number;
  skippedDecorative: number;
  total: number;
  aiPercentage: number;
}

interface OpenApiUsageData {
  content: OpenApiUsageItem[];
  totalPages: number;
  totalElements: number;
  number: number;
  size: number;
  sort?: { sortBy: SortBy; sortDir: SortDir; isDefault: boolean };
  stats?: AltTypeStats;
}

// ── 헬퍼 ────────────────────────────────────────────────────
const today = () => new Date().toISOString().slice(0, 10);
const oneMonthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
};

const sourceLabel = (t: string) =>
  t === 'imageUrl' ? '이미지 URL' : t === 'upload' ? '이미지 업로드' : t;

const generationBadge = (status: string) => {
  const cfg: Record<string, { className: string; label: string }> = {
    COMPLETED: { className: 'bg-green-100 text-green-800 border-green-300', label: '완료' },
    PENDING:   { className: 'bg-gray-100 text-gray-700 border-gray-300', label: '대기' },
    FAILED:    { className: 'bg-red-100 text-red-800 border-red-300', label: '실패' },
  };
  const c = cfg[status] ?? cfg.PENDING;
  return <Badge className={`border text-xs ${c.className}`}>{c.label}</Badge>;
};

export default function OpenApiUsagePage() {
  const { user, token } = useAuth();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const isSuperAdmin = user?.userRole === 'SUPER_ADMIN';

  // 조회 조건
  const [startDate, setStartDate] = useState(oneMonthAgo());
  const [endDate, setEndDate] = useState(today());
  const [sourceType, setSourceType] = useState<'' | SourceType>('');
  const [filterUserId, setFilterUserId] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'COMPLETED'>('ALL');
  const [altTypeFilter, setAltTypeFilter] = useState<'ALL' | 'AI' | 'FALLBACK' | 'PENDING' | 'ERROR' | 'SKIPPED_DECORATIVE'>('ALL');
  const [sortBy, setSortBy] = useState<SortBy>('UPDATED_AT');
  const [sortDir, setSortDir] = useState<SortDir>('DESC');

  // 상태
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<OpenApiUsageData | null>(null);
  const [allStats, setAllStats] = useState<AltTypeStats | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [regeneratingIds, setRegeneratingIds] = useState<Set<number>>(new Set());
  const [excelLoading, setExcelLoading] = useState(false);
  const [aiUsage, setAiUsage] = useState<AiUsageStatus | null>(null);

  // 커스텀 ALT 저장 핸들러
  const handleCustomAltSaved = (imageId: number, customAltText: string) => {
    setData(prev => 
      prev ? {
        ...prev,
        content: prev.content.map(item => 
          item.id === imageId 
            ? { ...item, customAltText } 
            : item
        )
      } : null
    );
  };

  // 커스텀 ALT 삭제 핸들러
  const handleCustomAltDeleted = (imageId: number) => {
    setData(prev => 
      prev ? {
        ...prev,
        content: prev.content.map(item => 
          item.id === imageId 
            ? { ...item, customAltText: null } 
            : item
        )
      } : null
    );
  };

  // ALT 우선순위: 커스텀 > 생성 > 원본
  const getDisplayAlt = (item: OpenApiUsageItem) => {
    if (item.customAltText) return item.customAltText;
    if (item.generatedAlt) return item.generatedAlt;
    return item.originalAlt || '';
  };

  // 항상 최신 필터 값을 유지하는 ref
  const filtersRef = useRef({ token, startDate, endDate, sourceType, filterUserId, isSuperAdmin, filter, altTypeFilter, sortBy, sortDir, pageSize });
  filtersRef.current = { token, startDate, endDate, sourceType, filterUserId, isSuperAdmin, filter, altTypeFilter, sortBy, sortDir, pageSize };

  const fetchData = useCallback(async (pageNum = 0, overrideFilter?: 'ALL' | 'PENDING' | 'COMPLETED', overrideAltType?: 'ALL' | 'AI' | 'FALLBACK' | 'PENDING' | 'ERROR' | 'SKIPPED_DECORATIVE') => {
    const { token: tk, startDate: sd, endDate: ed, sourceType: st, filterUserId: uid, isSuperAdmin: isAdmin, filter: fl, altTypeFilter: atf, sortBy: sb, sortDir: sdir, pageSize: ps } = filtersRef.current;
    if (!tk) return;
    setLoading(true);
    setError(null);

    const channelParam = '&channel=OPEN_API';
    const pageParam = `&page=${pageNum}`;
    const sizeParam = `&size=${ps}`;
    const startDateParam = sd ? `&startDate=${sd}` : '';
    const endDateParam = ed ? `&endDate=${ed}` : '';
    const sourceTypeParam = st ? `&sourceType=${st}` : '';
    const effectiveFilter = overrideFilter ?? fl;
    const effectiveAltType = overrideAltType ?? atf;
    const statusParam = effectiveFilter && effectiveFilter !== 'ALL' ? `&status=${effectiveFilter}` : '';
    const altTypeParam = effectiveAltType && effectiveAltType !== 'ALL' ? `&altGenerationType=${effectiveAltType}` : '';
    const sortParam = `&sortBy=${sb}&sortDir=${sdir}`;
    const userIdParam = isAdmin && uid.trim() ? `&userId=${uid.trim()}` : '';

    try {
      const res = await apiClient.get<OpenApiUsageData>(
        `/images?${channelParam}${pageParam}${sizeParam}${startDateParam}${endDateParam}${sourceTypeParam}${statusParam}${altTypeParam}${sortParam}${userIdParam}`
      );
      setData(res.data);
      setPage(pageNum);
      // fetchData 응답의 stats 재사용 (불필요한 API 호출 제거)
      if (res.data.stats) {
        setAllStats(res.data.stats);
      }
    } catch (e: any) {
      setError(e.message || '데이터 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []); // filtersRef를 통해 최신 값 읽으므로 deps 불필요

  const fetchStats = useCallback(async () => {
    const { token: tk, startDate: sd, endDate: ed, sourceType: st, filterUserId: uid, isSuperAdmin: isAdmin, filter: fl, altTypeFilter: atf, sortBy: sb, sortDir: sdir } = filtersRef.current;
    if (!tk) return;

    const channelParam = '&channel=OPEN_API';
    const pageParam = '&page=0';
    const sizeParam = '&size=999999';
    const startDateParam = sd ? `&startDate=${sd}` : '';
    const endDateParam = ed ? `&endDate=${ed}` : '';
    const sourceTypeParam = st ? `&sourceType=${st}` : '';
    const statusParam = fl && fl !== 'ALL' ? `&status=${fl}` : '';
    const altTypeParam = atf && atf !== 'ALL' ? `&altGenerationType=${atf}` : '';
    const sortParam = `&sortBy=${sb}&sortDir=${sdir}`;
    const userIdParam = isAdmin && uid.trim() ? `&userId=${uid.trim()}` : '';

    try {
      const res = await apiClient.get<OpenApiUsageData>(
        `/images?${channelParam}${pageParam}${sizeParam}${startDateParam}${endDateParam}${sourceTypeParam}${statusParam}${altTypeParam}${sortParam}${userIdParam}`
      );
      if (res.data.stats) {
        setAllStats(res.data.stats);
      }
    } catch {
      // 통계 조회 실패는 무시
    }
  }, []);

  const fetchAiUsage = async () => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/api/dashboard/ai-usage-status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAiUsage(response.data);
    } catch (err) {
      // 사용량 조회 실패 시 무시
    }
  };

  useEffect(() => {
    fetchData(0);
    fetchAiUsage();
  }, [fetchData]); // fetchData는 stable → 마운트 시 1회만 실행

  const handleSearch = () => fetchData(0);

  const handleCopy = async (text: string, id: number) => {
    await navigator.clipboard.writeText(text).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyAlt = async (text: string, id: number) => {
    await navigator.clipboard.writeText(text).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyHtml = async (alt: string, id: number) => {
    const html = `alt="${alt}"`;
    await navigator.clipboard.writeText(html).catch(() => {});
    setCopiedId(-id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!window.confirm('이 이미지를 삭제하시겠습니까?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const res = await fetch(`${BASE}/api/crawler/images/${imageId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      fetchData(page);
      alert('이미지가 삭제되었습니다.');
    } catch {
      alert('이미지를 삭제하지 못했습니다.');
    }
  };

  const handleDoubleClick = async (text: string) => {
    await navigator.clipboard.writeText(text).catch(() => {});
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <Badge className="bg-green-100 text-green-800 border border-green-200 text-xs">완료</Badge>;
      case 'PENDING':
        return <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200 text-xs">대기</Badge>;
      case 'FAILED':
        return <Badge className="bg-red-100 text-red-800 border border-red-200 text-xs">실패</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800 border border-gray-200 text-xs">{status}</Badge>;
    }
  };

  const getAltTypeBadge = (type?: string) => {
    switch (type) {
      case 'AI':
        return <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-xs">✓ AI 분석</Badge>;
      case 'FALLBACK':
        return <Badge className="bg-orange-100 text-orange-800 border border-orange-200 text-xs">⚠ 임시 생성</Badge>;
      case 'ERROR':
        return <Badge className="bg-red-100 text-red-800 border border-red-200 text-xs">✗ 오류</Badge>;
      case 'SKIPPED_DECORATIVE':
        return <Badge className="bg-purple-100 text-purple-800 border border-purple-200 text-xs">🎨 장식용 이미지</Badge>;
      default:
        return null;
    }
  };

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString('ko-KR');
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getAltTypeStats = () => {
    if (!allStats) {
      return { ai: 0, fallback: 0, pending: 0, error: 0, skippedDecorative: 0, total: 0, aiPercentage: 0 };
    }
    return allStats;
  };

  const handleExcelDownload = async () => {
    setExcelLoading(true);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const authHeaders: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

      // 전체 페이지 순차 fetch (엑셀 다운로드는 전체 데이터)
      let allData: OpenApiUsageItem[] = [];
      let fetchPage = 0;
      const { token: tk, startDate: sd, endDate: ed, filterUserId: uid, isSuperAdmin: isAdmin, sortBy: sb, sortDir: sdir } = filtersRef.current;
      
      while (true) {
        const channelParam = '&channel=OPEN_API';
        const pageParam = `&page=${fetchPage}`;
        const sizeParam = '&size=999999';
        const startDateParam = sd ? `&startDate=${sd}` : '';
        const endDateParam = ed ? `&endDate=${ed}` : '';
        const sortParam = `&sortBy=${sb}&sortDir=${sdir}`;
        const userIdParam = isAdmin && uid.trim() ? `&userId=${uid.trim()}` : '';

        const batchRes = await apiClient.get<OpenApiUsageData>(
          `/images?${channelParam}${pageParam}${sizeParam}${startDateParam}${endDateParam}${sortParam}${userIdParam}`
        );
        allData = allData.concat(batchRes.data.content || []);
        if (fetchPage >= (batchRes.data.totalPages || 0) - 1 || (batchRes.data.content?.length || 0) === 0) break;
        fetchPage++;
      }

      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('이미지목록');
      ws.columns = [
        { header: 'ID', key: 'id', width: 8 },
        { header: '이미지 URL', key: 'imageUrl', width: 55 },
        { header: '기존 ALT', key: 'origAlt', width: 30 },
        { header: 'AI 생성 ALT', key: 'alt', width: 50 },
        { header: '생성 방식', key: 'type', width: 15 },
        { header: '상태', key: 'status', width: 12 },
        { header: '소스 타입', key: 'sourceType', width: 15 },
        { header: '최종 수정일', key: 'updatedAt', width: 22 },
      ];
      ws.getRow(1).font = { bold: true };
      ws.getRow(1).height = 18;

      for (let i = 0; i < allData.length; i++) {
        const img = allData[i];
        const row = ws.addRow({
          id: img.id,
          imageUrl: img.url,
          origAlt: img.originalAlt ?? '',
          alt: img.generatedAlt ?? '',
          type: img.altGenerationType ?? '',
          status: img.status ?? '',
          sourceType: img.sourceType ?? '',
          updatedAt: new Date(img.updatedAt).toLocaleString('ko-KR'),
        });
        if (img.url?.startsWith('http')) {
          row.getCell('imageUrl').value = { text: img.url, hyperlink: img.url };
          row.getCell('imageUrl').font = { color: { argb: 'FF0563C1' }, underline: true };
        }
      }

      const buf = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `OpenAPI_이미지목록_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(dlUrl);
    } catch {
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setExcelLoading(false);
    }
  };

  const handleRegenerateAlt = async (imageId: number, force: boolean = false) => {
    if (!window.confirm(force ? '캐시를 무시하고 강제로 AI 재분석을 시도하시겠습니까?' : 'AI를 통한 ALT 생성을 다시 시도하시겠습니까?')) {
      return;
    }

    // 팀 컨텍스트인 경우 팀 한도 기준으로 체크
    if (aiUsage) {
      if (aiUsage.isTeamContext) {
        const teamLimit = aiUsage.teamLimit ?? aiUsage.limit;
        const teamUsed = aiUsage.teamTotalUsed ?? aiUsage.used;
        if (teamUsed >= teamLimit) {
          alert(`팀 AI 사용 한도(${teamLimit}건)를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.`);
          return;
        }
      } else if (!aiUsage.canUse) {
        alert(`이번 달 AI 사용 한도(${aiUsage.limit}건)를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.`);
        return;
      }
    }

    setRegeneratingIds(prev => new Set(prev).add(imageId));
    try {
      // force인 경우 force-refresh 엔드포인트 사용
      const endpoint = force 
        ? `${BASE}/api/crawler/images/${imageId}/force-refresh`
        : `${BASE}/api/crawler/images/${imageId}/regenerate`;
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || `HTTP ${res.status}`);
      }
      const json = await res.json();

      // SSE 연결로 실시간 상태 수신
      sseService.connect(imageId, {
        onConnected: (data) => {
          console.log('[SSE] 재분석 연결됨:', data);
        },
        onStatusUpdate: (data) => {
          if (data.status === 'FAILED' && data.errorType) {
            // 에러 코드에 따른 알림 표시
            let errorMessage = '이미지 분석에 실패했습니다.';
            if (data.errorType === 'MONTHLY_LIMIT_EXCEEDED') {
              errorMessage = '월별 한도를 초과했습니다. 요금제 업그레이드를 진행해 주세요.';
            } else if (data.errorType === 'DAILY_LIMIT_EXCEEDED') {
              errorMessage = '일별 한도를 초과했습니다. 내일 다시 시도해주세요.';
            } else if (data.errorType === 'ENTERPRISE_LIMIT_EXCEEDED') {
              errorMessage = '엔터프라이즈 한도를 초과했습니다. 엔터프라이즈 계약서 수정으로 이용가능한 건수를 추가해 주세요.';
            }
            alert(errorMessage);
          }

          if (data.status === 'COMPLETED' || data.status === 'FAILED') {
            fetchData(page);
            setRegeneratingIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(imageId);
              return newSet;
            });
            sseService.disconnect();
          }
        },
        onTimeout: (data) => {
          console.log('[SSE] 타임아웃:', data);
          fetchData(page);
          setRegeneratingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(imageId);
            return newSet;
          });
        },
        onError: (error) => {
          console.error('[SSE] 에러:', error);
          fetchData(page);
          setRegeneratingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(imageId);
            return newSet;
          });
        }
      });
    } catch (e: any) {
      setError(e.message || '재분석 실패');
      setRegeneratingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    }
  };

  const handleForceRefresh = async (imageId: number) => {
    if (!window.confirm('이미 AI로 생성한 ALT를 다시 생성하여 ALT를 업데이트 하시겠습니까? (AI 사용량이 차감됩니다)')) {
      return;
    }

    setRegeneratingIds(prev => new Set(prev).add(imageId));
    try {
      const res = await fetch(`${BASE}/api/crawler/images/${imageId}/force-refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || `HTTP ${res.status}`);
      }
      const json = await res.json();

      // SSE 연결로 실시간 상태 수신
      sseService.connect(imageId, {
        onConnected: (data) => {
          console.log('[SSE] 강제 재분석 연결됨:', data);
        },
        onStatusUpdate: (data) => {
          if (data.status === 'FAILED' && data.errorType) {
            // 에러 코드에 따른 알림 표시
            let errorMessage = '이미지 분석에 실패했습니다.';
            if (data.errorType === 'MONTHLY_LIMIT_EXCEEDED') {
              errorMessage = '월별 한도를 초과했습니다. 요금제 업그레이드를 진행해 주세요.';
            } else if (data.errorType === 'DAILY_LIMIT_EXCEEDED') {
              errorMessage = '일별 한도를 초과했습니다. 내일 다시 시도해주세요.';
            } else if (data.errorType === 'ENTERPRISE_LIMIT_EXCEEDED') {
              errorMessage = '엔터프라이즈 한도를 초과했습니다. 엔터프라이즈 계약서 수정으로 이용가능한 건수를 추가해 주세요.';
            }
            alert(errorMessage);
          }

          if (data.status === 'COMPLETED' || data.status === 'FAILED') {
            fetchData(page);
            setRegeneratingIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(imageId);
              return newSet;
            });
            sseService.disconnect();
          }
        },
        onTimeout: (data) => {
          console.log('[SSE] 타임아웃:', data);
          fetchData(page);
          setRegeneratingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(imageId);
            return newSet;
          });
        },
        onError: (error) => {
          console.error('[SSE] 에러:', error);
          fetchData(page);
          setRegeneratingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(imageId);
            return newSet;
          });
        }
      });
    } catch (e: any) {
      setError(e.message || 'ALT 강제 재생성 실패');
      setRegeneratingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    }
  };

  const truncate = (str: string, max = 50) =>
    str && str.length > max ? str.slice(0, max) + '…' : str;

  // 이미지 URL 변환 함수
  const getImageUrl = (item: OpenApiUsageItem) => {
    const src = item.imageSrc || item.url || item.displayImageUrl;
    
    // 외부 URL인 경우 그대로 사용
    if (src?.startsWith('http') && !src?.includes('/api/')) return src;
    
    // 이미 API 경로인 경우 그대로 사용
    if (src?.startsWith('/api/')) return `${BASE}${src}`;
    
    // 로컬 절대 경로인 경우 (C:\...) 파일명만 추출하여 날짜 경로와 결합
    if (src?.includes(':\\')) {
      const filename = src.split(/[\\/]/).pop() || src;
      // sample-tests 디렉토리인 경우 새 엔드포인트 사용
      if (src.includes('sample-tests')) {
        return `${BASE}/api/crawler/uploads/sample-tests/${filename}`;
      }
      // createdAt에서 날짜 경로 추출 (YYYY/MM)
      const datePath = item.createdAt ? item.createdAt.substring(0, 7).replace('-', '/') : '';
      return `${BASE}/api/crawler/uploads/${datePath}/${filename}`;
    }
    
    // 파일명만 있는 경우 (로컬 경로가 아닌 경우) 날짜 경로와 결합
    const filename = src?.split(/[\\/]/).pop() || src;
    const datePath = item.createdAt ? item.createdAt.substring(0, 7).replace('-', '/') : '';
    return `${BASE}/api/crawler/uploads/${datePath}/${filename}`;
  };

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || 'API 이용 현황'}
        description="API 호출 이력 (이미지 URL · 이미지 업로드)"
        icon={menuIcon}
      />

      {/* AI 사용량 표시 */}
      {aiUsage && (
        <div className={`flex flex-wrap items-center justify-between gap-2 px-4 py-2 rounded-lg text-sm border mt-4 ${
          aiUsage.isTeamContext
            ? (aiUsage.teamTotalUsed !== null && aiUsage.teamLimit !== null && aiUsage.teamTotalUsed >= aiUsage.teamLimit)
              ? 'bg-red-50 border-red-300'
              : 'bg-blue-50 border-blue-200'
            : (!aiUsage.canUse ? 'bg-red-50 border-red-300' :
            (aiUsage.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))
              ? 'bg-orange-50 border-orange-300'
              : 'bg-gray-50 border-gray-200')
        }`}>
          <span className="text-gray-700">
            {aiUsage.isTeamContext ? (
              <>
                {aiUsage.teamTotalUsed !== null && aiUsage.teamLimit !== null ? (
                  <>
                    팀 사용량: <strong>{aiUsage.teamTotalUsed.toLocaleString()}</strong> / {aiUsage.teamLimit.toLocaleString()}건
                    <span className="ml-2 text-gray-500">(본인: {aiUsage.used.toLocaleString()}건)</span>
                  </>
                ) : (
                  <>
                    본인 사용량: <strong>{aiUsage.used.toLocaleString()}</strong> / {aiUsage.limit.toLocaleString()}건
                  </>
                )}
                <span className="ml-2 text-gray-500">({aiUsage.teamName} 팀, {aiUsage.role === 'OWNER' ? '팀장' : '팀원'}, {aiUsage.plan})</span>
              </>
            ) : (
              <>
                이번 달 AI 사용량: <strong>{aiUsage.used.toLocaleString()}</strong> / {aiUsage.limit.toLocaleString()}건
                <span className="ml-2 text-gray-500">({aiUsage.plan} 플랜입니다)</span>
              </>
            )}
          </span>
          {aiUsage.isTeamContext ? (
            aiUsage.teamTotalUsed !== null && aiUsage.teamLimit !== null && aiUsage.teamTotalUsed >= aiUsage.teamLimit && (
              <span className="text-red-600 font-semibold text-xs">⚠️ 팀 한도 초과 &mdash; 관리자에게 문의해 주세요.</span>
            )
          ) : (
            !aiUsage.canUse && (
              <span className="text-red-600 font-semibold text-xs">⚠️ 한도 초과 &mdash; 관리자에게 문의해 주세요.</span>
            )
          )}
          {!aiUsage.isTeamContext && aiUsage.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (
            (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0) ? (
              <span className="text-orange-600 font-semibold text-xs">
                ⚠️ 오늘 {(aiUsage.dailyUsed ?? 0)}/{aiUsage.dailyLimit}건 &mdash; 일일 한도 초과. 내일 다시 이용하거나 플랜을 업그레이드하세요.
              </span>
            ) : (
              <span className="text-gray-500 text-xs">
                오늘 {(aiUsage.dailyUsed ?? 0)}/{aiUsage.dailyLimit}건 (무료 플랜 일일 한도)
              </span>
            )
          )}
        </div>
      )}

      {/* 엔터프라이즈 계약 정보 */}
      {aiUsage?.enterpriseContract && (
        <div className="px-4 py-2 rounded-lg text-sm border mt-2 bg-purple-50 border-purple-200">
          <span className="text-purple-700">
            <strong>엔터프라이즈 계약:</strong> {aiUsage.enterpriseContract.contractName} |
            월 한도: {aiUsage.enterpriseContract.monthlyLimit.toLocaleString()}건 |
            월 이용료: ₩{aiUsage.enterpriseContract.monthlyPrice.toLocaleString()}
          </span>
        </div>
      )}

      {/* 조회 조건 */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label>시작일</Label>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label>종료일</Label>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label>유형</Label>
              <select
                value={sourceType}
                onChange={e => setSourceType(e.target.value as '' | SourceType)}
                className="border rounded-md px-3 h-10 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary w-40"
              >
                <option value="">전체</option>
                <option value="imageUrl">이미지 URL</option>
                <option value="upload">이미지 업로드</option>
              </select>
            </div>
            {isSuperAdmin && (
              <div className="space-y-1">
                <Label>사용자 ID (SUPER_ADMIN)</Label>
                <Input
                  type="text"
                  placeholder="전체 (비워두면 모든 사용자)"
                  value={filterUserId}
                  onChange={e => setFilterUserId(e.target.value)}
                  className="w-56"
                />
              </div>
            )}
            <Button onClick={handleSearch} disabled={loading} className="h-10">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              조회
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 결과 목록 */}
      {data && (
        <>
          {/* ALT 텍스트 생성 통계 */}
          {data?.content && data.content.length > 0 && (() => {
            const stats = getAltTypeStats();
            return (
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
                <CardHeader>
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                      <CardTitle className="text-lg">ALT 텍스트 생성 통계
                        <span className="text-sm font-normal text-gray-500 ml-2">— 현재 페이지 기준</span>
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center p-4 bg-white rounded-lg">
                      <p className="text-3xl font-bold text-blue-600">{stats.ai.toLocaleString()}</p>
                      <p className="text-sm text-gray-600 mt-1">AI 분석</p>
                      <p className="text-xs text-gray-400 mt-0.5">{stats.aiPercentage}% 정확도</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg">
                      <p className="text-3xl font-bold text-orange-600">{stats.fallback.toLocaleString()}</p>
                      <p className="text-sm text-gray-600 mt-1">임시 생성</p>
                      <p className="text-xs text-gray-400 mt-0.5">재분석 권장</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg">
                      <p className="text-3xl font-bold text-gray-600">{stats.pending.toLocaleString()}</p>
                      <p className="text-sm text-gray-600 mt-1">미생성</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg">
                      <p className="text-3xl font-bold text-red-600">{stats.error.toLocaleString()}</p>
                      <p className="text-sm text-gray-600 mt-1">오류</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg">
                      <p className="text-3xl font-bold text-purple-600">{stats.skippedDecorative.toLocaleString()}</p>
                      <p className="text-sm text-gray-600 mt-1">장식용 이미지</p>
                      <p className="text-xs text-gray-400 mt-0.5">ALT 생성 건너뜀</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <CardTitle className="text-xl">이미지 목록({data.totalElements?.toLocaleString() || 0}건)</CardTitle>
                <div className="flex gap-2">
                  {data?.content && data.content.length > 0 && (
                    <Button
                      onClick={handleExcelDownload}
                      disabled={excelLoading}
                      variant="outline"
                      size="sm"
                      className="text-green-700 border-green-400 hover:bg-green-50"
                    >
                      {excelLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />다운로드 중...</>
                      ) : (
                        <><Download className="w-4 h-4 mr-2" />엑셀 다운로드 ({data.totalElements?.toLocaleString() || 0}건)</>
                      )}
                    </Button>
                  )}
                  <Button
                    onClick={() => { setAltTypeFilter('ALL'); fetchData(0, undefined, 'ALL'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'ALL' ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600' : ''}
                  >
                    전체
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('AI'); fetchData(0, undefined, 'AI'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'AI' ? 'bg-green-600 text-white hover:bg-green-700 border-green-600' : ''}
                  >
                    ✓ AI 분석
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('FALLBACK'); fetchData(0, undefined, 'FALLBACK'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'FALLBACK' ? 'bg-yellow-600 text-white hover:bg-yellow-700 border-yellow-600' : ''}
                  >
                    ⚠ 임시 생성
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('PENDING'); fetchData(0, undefined, 'PENDING'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'PENDING' ? 'bg-gray-600 text-white hover:bg-gray-700 border-gray-600' : ''}
                  >
                    📝 미생성
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('ERROR'); fetchData(0, undefined, 'ERROR'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'ERROR' ? 'bg-red-600 text-white hover:bg-red-700 border-red-600' : ''}
                  >
                    ✗ 오류
                  </Button>
                  <Button
                    onClick={() => { setAltTypeFilter('SKIPPED_DECORATIVE'); fetchData(0, undefined, 'SKIPPED_DECORATIVE'); }}
                    variant="outline"
                    size="sm"
                    className={altTypeFilter === 'SKIPPED_DECORATIVE' ? 'bg-purple-600 text-white hover:bg-purple-700 border-purple-600' : ''}
                  >
                    🎨 장식용
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
            <Tabs value={filter} onValueChange={(v) => {
              const newFilter = v as 'ALL' | 'PENDING' | 'COMPLETED';
              setFilter(newFilter);
              fetchData(0, newFilter);
            }} className="w-full">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                <TabsList className="grid grid-cols-3 flex-1">
                  <TabsTrigger value="ALL">전체</TabsTrigger>
                  <TabsTrigger value="PENDING">대기</TabsTrigger>
                  <TabsTrigger value="COMPLETED">완료</TabsTrigger>
                </TabsList>
                <div className="flex items-center gap-1 shrink-0">
                  <Select
                    value={`${sortBy}_${sortDir}`}
                    onValueChange={(v) => {
                      const lastIdx = v.lastIndexOf('_');
                      const newSortBy = v.substring(0, lastIdx) as SortBy;
                      const newSortDir = v.substring(lastIdx + 1) as SortDir;
                      setSortBy(newSortBy);
                      setSortDir(newSortDir);
                      fetchData(0);
                    }}
                  >
                    <SelectTrigger className="w-[180px] h-9 text-sm">
                      <SelectValue placeholder="정렬" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UPDATED_AT_DESC">수정일 최신순</SelectItem>
                      <SelectItem value="UPDATED_AT_ASC">수정일 오래된순</SelectItem>
                      <SelectItem value="CREATED_AT_DESC">등록일 최신순</SelectItem>
                      <SelectItem value="CREATED_AT_ASC">등록일 오래된순</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <TabsContent value={filter} className="mt-6">
                {(() => {
                  const filteredImages = data.content || [];
                  console.log('[OpenApiUsage] filter:', filter, 'data.content length:', data.content?.length, 'filteredImages length:', filteredImages.length);
                  
                  if (filteredImages.length === 0) {
                    return (
                      <div className="text-center py-12 text-gray-500">
                        <FileImage className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>{data.content?.length === 0 ? '조회된 데이터가 없습니다.' : '해당 조건의 이미지가 없습니다.'}</p>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="space-y-4">
                      {filteredImages.map((item) => (
                      <Card key={item.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex flex-col md:flex-row gap-4">
                            <AuthenticatedImage
                              src={getImageUrl(item)}
                              alt={item.generatedAlt || ''}
                              className="w-full md:w-32 h-32 object-cover rounded-lg"
                            />
                            <div className="flex-1 space-y-2">
                              <div className="flex gap-2 flex-wrap">
                                {getStatusBadge(item.status)}
                                {getAltTypeBadge(item.altGenerationType)}
                                {item.originalAlt !== null && item.originalAlt !== undefined ? (
                                  item.originalAlt === '' ? (
                                    <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-200 text-xs">기존 ALT: 빈값</Badge>
                                  ) : (
                                    <Badge className="bg-green-100 text-green-700 border border-green-200 text-xs">기존 ALT 있음 ✓</Badge>
                                  )
                                ) : (
                                  <Badge className="bg-red-100 text-red-700 border border-red-200 text-xs">ALT 누락 ⚠</Badge>
                                )}
                                {item.cachecnt !== undefined && (
                                  <Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-xs">
                                    캐시 {item.cachecnt}회
                                  </Badge>
                                )}
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">
                                  <span className="font-semibold">{item.sourceType === 'upload' ? '원본 파일:' : '이미지 URL:'}</span> {item.sourceType === 'upload' ? (item.url?.split(/[\\/]/).pop() || item.url) : item.url}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  최종 수정: {formatRelativeTime(item.updatedAt)} ({formatDateTime(item.updatedAt)})
                                </p>
                              </div>
                              {item.originalAlt !== null && item.originalAlt !== undefined && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 mb-1">📄 기존 ALT</p>
                                  <p className="text-sm text-gray-700 p-2 rounded bg-white/50 break-words">
                                    {item.originalAlt || '(빈값)'}
                                  </p>
                                </div>
                              )}
                              {item.status === 'PENDING' ? (
                                regeneratingIds.has(item.id) ? (
                                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                                    <p className="text-sm text-blue-800 font-medium">⏳ AI가 ALT 텍스트를 분석 중입니다.</p>
                                    <p className="text-xs text-blue-600 mt-1">잠시 후 자동으로 결과가 표시됩니다.</p>
                                  </div>
                                ) : (
                                  <Button
                                    onClick={() => handleRegenerateAlt(item.id)}
                                    size="sm"
                                    variant="outline"
                                    className="text-blue-600 border-blue-300 hover:bg-blue-50 whitespace-nowrap"
                                    disabled={regeneratingIds.has(item.id)}
                                  >
                                    {regeneratingIds.has(item.id) ? (
                                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" />분석중...</>
                                    ) : (
                                      <><RefreshCw className="w-3 h-3 mr-1" />ALT 생성</>
                                    )}
                                  </Button>
                                )
                              ) : item.altGenerationType === 'SKIPPED_DECORATIVE' ? (
                                <div className="rounded-lg border bg-purple-50 p-3">
                                  <p className="text-xs font-semibold text-purple-500 mb-1">🎨 장식용 이미지</p>
                                  <p className="text-sm text-purple-700">ALT 텍스트 생성이 건너뛰었습니다.</p>
                                </div>
                              ) : item.generatedAlt ? (
                                <Card className={`${
                                  item.altGenerationType === 'AI' ? 'bg-green-50 border-green-200' :
                                  item.altGenerationType === 'FALLBACK' ? 'bg-yellow-50 border-yellow-200' :
                                  'bg-gray-50 border-gray-200'
                                }`}>
                                  <CardContent className="p-3">
                                    <div className="flex flex-col sm:flex-row gap-2">
                                      <div className="flex-1 min-w-0">
                                        <p 
                                          className={`text-sm mb-1 p-2 rounded transition-colors ${
                                            item.altGenerationType === 'AI' && item.status === 'COMPLETED' ? 'font-semibold' : ''
                                          } ${
                                            item.altGenerationType === 'AI' ? 'text-green-800' :
                                            item.altGenerationType === 'FALLBACK' ? 'text-yellow-800' :
                                            item.altGenerationType === 'ERROR' ? 'text-red-600' :
                                            'text-gray-800'
                                          } ${
                                            item.altGenerationType !== 'ERROR' ? 'cursor-pointer hover:bg-white/50' : ''
                                          }`}
                                          {...(item.altGenerationType !== 'ERROR' && {
                                            onDoubleClick: () => handleDoubleClick(item.generatedAlt!),
                                            title: '더블클릭하여 복사'
                                          })}
                                        >
                                          {item.generatedAlt}
                                        </p>
                                        {item.altGenerationType === 'FALLBACK' && (
                                          <p className="text-xs text-yellow-700 mt-2 italic">
                                            💡 이 설명은 URL 기반으로 임시 생성되었습니다. 정확하지 않을 수 있습니다.
                                          </p>
                                        )}
                                      </div>
                                      {/* 커스텀 ALT 섹션 */}
                                      <div className="mt-4">
                                        <p className="text-xs font-semibold text-gray-500 mb-2">✏️ 커스텀 ALT</p>
                                        <CustomAltEditor
                                          imageId={item.id}
                                          customAltText={item.customAltText}
                                          onSaved={(customAltText) => handleCustomAltSaved(item.id, customAltText)}
                                          onDeleted={() => handleCustomAltDeleted(item.id)}
                                        />
                                      </div>
                                      <div className="flex flex-wrap gap-2 shrink-0">
                                        <Button
                                          onClick={() => handleCopyAlt(getDisplayAlt(item), item.id)}
                                          size="sm"
                                          variant="outline"
                                          className="whitespace-nowrap"
                                        >
                                          {copiedId === item.id ? (
                                            <>
                                              <Check className="w-3 h-3 mr-1 text-green-600" />
                                              복사됨
                                            </>
                                          ) : (
                                            <>
                                              <Copy className="w-3 h-3 mr-1" />
                                              ALT 복사
                                            </>
                                          )}
                                        </Button>
                                        <Button
                                          onClick={() => handleCopyHtml(getDisplayAlt(item), item.id)}
                                          size="sm"
                                          variant="outline"
                                          className="whitespace-nowrap"
                                        >
                                          {copiedId === -item.id ? (
                                            <>
                                              <Check className="w-3 h-3 mr-1 text-green-600" />
                                              복사됨
                                            </>
                                          ) : (
                                            <>
                                              <Copy className="w-3 h-3 mr-1" />
                                              HTML 복사
                                            </>
                                          )}
                                        </Button>
                                        {item.cachecnt !== undefined && item.cachecnt > 0 && (
                                          <Button
                                            onClick={() => handleForceRefresh(item.id)}
                                            variant="outline"
                                            size="sm"
                                            className="text-purple-600 border-purple-300 hover:bg-purple-50 whitespace-nowrap"
                                            disabled={regeneratingIds.has(item.id)}
                                          >
                                            {regeneratingIds.has(item.id) ? (
                                              <><Loader2 className="w-4 h-4 mr-1 animate-spin" />강제 재분석 중...</>
                                            ) : (
                                              <>🔄 강제 재분석</>
                                            )}
                                          </Button>
                                        )}
                                        <Button
                                          onClick={() => handleDeleteImage(item.id)}
                                          variant="destructive"
                                          size="sm"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ) : (
                                <div className="rounded-lg border bg-gray-50 p-3">
                                  <p className="text-xs font-semibold text-gray-500 mb-1">🤖 AI 생성 ALT</p>
                                  <p className="text-sm text-gray-400">-</p>
                                </div>
                              )}
                              {item.status !== 'COMPLETED' && (!item.cachecnt || item.cachecnt === 0) && (
                                <Button
                                  onClick={() => handleRegenerateAlt(item.id)}
                                  variant="outline"
                                  size="sm"
                                  className="text-blue-600 border-blue-300 hover:bg-blue-50 whitespace-nowrap"
                                  disabled={regeneratingIds.has(item.id)}
                                >
                                  {regeneratingIds.has(item.id) ? (
                                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" />재분석 중...</>
                                  ) : (
                                    <><RefreshCw className="w-4 h-4 mr-1" />재분석</>
                                  )}
                                </Button>
                              )}
                              {item.cachecnt !== undefined && item.cachecnt > 0 && (
                                <Button
                                  onClick={() => handleRegenerateAlt(item.id, true)}
                                  variant="outline"
                                  size="sm"
                                  className="text-purple-600 border-purple-300 hover:bg-purple-50 whitespace-nowrap"
                                  disabled={regeneratingIds.has(item.id)}
                                >
                                  {regeneratingIds.has(item.id) ? (
                                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" />강제 재분석 중...</>
                                  ) : (
                                    <><Loader2 className="w-4 h-4 mr-1" />강제 재분석</>
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  );
                })()}
              </TabsContent>
            </Tabs>
            {/* 페이지네이션 */}
            {data.content && data.content.length > 0 && (
              <div className="flex justify-between items-center mt-6">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>페이지당</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => {
                      const newSize = Number(v);
                      setPageSize(newSize);
                      setPage(0);
                      fetchData(0);
                    }}
                  >
                    <SelectTrigger className="w-20 h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10개</SelectItem>
                      <SelectItem value="20">20개</SelectItem>
                      <SelectItem value="50">50개</SelectItem>
                      <SelectItem value="100">100개</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(data.totalPages || 1) > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchData(page - 1)}
                      disabled={page === 0 || loading}
                    >
                      이전
                    </Button>
                    <span className="text-sm text-gray-600">
                      {page + 1} / {data.totalPages || 1}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchData(page + 1)}
                      disabled={page >= (data.totalPages || 1) - 1 || loading}
                    >
                      다음
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        </>
      )}
    </PageContainer>
  );
}
